<template>
  <el-dialog
    :title="`分佣配置 - ${shopName}`"
    :visible.sync="dialogVisible"
    width="1200px"
    @close="handleClose"
  >
    <div v-loading="loading">
      <!-- 说明 -->
      <el-alert
        title="分佣说明"
        type="info"
        :closable="false"
        style="margin-bottom: 20px"
      >
        <div style="font-size: 12px; line-height: 1.8">
          1. 分佣类型：百分比（按订单金额百分比）、固定金额（每笔订单固定金额）<br>
          2. 特殊分佣优先级高于默认分佣<br>
          3. 可以为每个套餐单独设置特殊分佣，或清除特殊分佣恢复默认
        </div>
      </el-alert>

      <!-- 套餐列表 -->
      <el-table
        :data="packageList"
        border
        style="width: 100%"
      >
        <!-- 套餐编码 -->
        <el-table-column
          prop="package_code"
          label="套餐编码"
          width="120"
          align="center"
        />

        <!-- 套餐名称 -->
        <el-table-column
          prop="package_name"
          label="套餐名称"
          width="150"
          align="center"
          show-overflow-tooltip
        />

        <!-- 套餐价格 -->
        <el-table-column
          prop="price"
          label="价格(元)"
          width="100"
          align="center"
        >
          <template slot-scope="{ row }">
            {{ row.price }}
          </template>
        </el-table-column>

        <!-- 默认分佣 -->
        <el-table-column
          label="默认分佣"
          width="120"
          align="center"
        >
          <template slot-scope="{ row }">
            <span v-if="row.default_commission_type === 1">
              {{ row.default_commission_value }}%
            </span>
            <span v-else-if="row.default_commission_type === 2">
              ¥{{ row.default_commission_value }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <!-- 特殊分佣配置 -->
        <el-table-column
          label="特殊分佣"
          width="150"
          align="center"
        >
          <template slot-scope="{ row }">
            <el-tag v-if="row.commission_type" type="success" size="small">
              <span v-if="row.commission_type === 1">
                {{ row.commission_value }}%
              </span>
              <span v-else-if="row.commission_type === 2">
                ¥{{ row.commission_value }}
              </span>
            </el-tag>
            <span v-else style="color: #909399">使用默认</span>
          </template>
        </el-table-column>

        <!-- 销售统计 -->
        <el-table-column
          label="销售数量"
          width="100"
          align="center"
        >
          <template slot-scope="{ row }">
            {{ row.sales_count || 0 }}
          </template>
        </el-table-column>

        <el-table-column
          label="销售金额(元)"
          width="120"
          align="center"
        >
          <template slot-scope="{ row }">
            {{ row.sales_amount || 0 }}
          </template>
        </el-table-column>

        <el-table-column
          label="总佣金(元)"
          width="120"
          align="center"
        >
          <template slot-scope="{ row }">
            {{ row.total_commission || 0 }}
          </template>
        </el-table-column>

        <!-- 操作列 -->
        <el-table-column
          label="操作"
          width="180"
          align="center"
          fixed="right"
        >
          <template slot-scope="{ row }">
            <el-button
              type="text"
              size="small"
              @click="handleSetCommission(row)"
            >
              设置特殊分佣
            </el-button>
            <el-button
              v-if="row.commission_type"
              type="text"
              size="small"
              @click="handleClearCommission(row)"
            >
              清除
            </el-button>
            <el-button
              type="text"
              size="small"
              @click="handleViewLogs(row)"
            >
              调整记录
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">关闭</el-button>
    </div>

    <!-- 设置分佣弹窗 -->
    <el-dialog
      title="设置特殊分佣"
      :visible.sync="setCommissionVisible"
      width="500px"
      append-to-body
    >
      <el-form
        ref="commissionForm"
        :model="commissionForm"
        :rules="commissionRules"
        label-width="120px"
      >
        <el-form-item label="套餐">
          <el-input :value="currentPackage.package_name" disabled />
        </el-form-item>

        <el-form-item label="分佣类型" prop="commission_type">
          <el-radio-group v-model="commissionForm.commission_type">
            <el-radio :label="1">百分比</el-radio>
            <el-radio :label="2">固定金额</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="分佣值" prop="commission_value">
          <el-input-number
            v-model="commissionForm.commission_value"
            :precision="2"
            :step="1"
            :min="0"
            :max="commissionForm.commission_type === 1 ? 100 : 999999"
            controls-position="right"
            style="width: 100%"
          />
          <div style="margin-top: 5px; font-size: 12px; color: #909399">
            <span v-if="commissionForm.commission_type === 1">
              百分比：0-100，如 15 表示 15%
            </span>
            <span v-else>
              固定金额：单位为元
            </span>
          </div>
        </el-form-item>

        <el-form-item label="调整原因" prop="adjust_reason">
          <el-input
            v-model="commissionForm.adjust_reason"
            type="textarea"
            :rows="3"
            placeholder="请输入调整原因（选填）"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="setCommissionVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="handleSubmitCommission">
          确定
        </el-button>
      </div>
    </el-dialog>

    <!-- 调整记录弹窗 -->
    <el-dialog
      title="分佣调整记录"
      :visible.sync="logsVisible"
      width="900px"
      append-to-body
    >
      <el-table
        v-loading="logsLoading"
        :data="logsList"
        border
        style="width: 100%"
      >
        <el-table-column
          prop="created_at"
          label="调整时间"
          width="180"
          align="center"
        />

        <el-table-column
          prop="operator_name"
          label="操作人"
          width="120"
          align="center"
        />

        <el-table-column
          prop="action_type"
          label="操作类型"
          width="100"
          align="center"
        >
          <template slot-scope="{ row }">
            <el-tag :type="row.action_type === 'create' ? 'success' : 'warning'" size="small">
              {{ row.action_type === 'create' ? '创建' : '更新' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column
          label="调整前"
          width="120"
          align="center"
        >
          <template slot-scope="{ row }">
            <span v-if="row.old_commission_type === 1">
              {{ row.old_commission_value }}%
            </span>
            <span v-else-if="row.old_commission_type === 2">
              ¥{{ row.old_commission_value }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <el-table-column
          label="调整后"
          width="120"
          align="center"
        >
          <template slot-scope="{ row }">
            <span v-if="row.new_commission_type === 1">
              {{ row.new_commission_value }}%
            </span>
            <span v-else-if="row.new_commission_type === 2">
              ¥{{ row.new_commission_value }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="adjust_reason"
          label="调整原因"
          align="center"
          show-overflow-tooltip
        />
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          :current-page="logsQuery.page"
          :page-sizes="[10, 20, 50]"
          :page-size="logsQuery.page_size"
          :total="logsTotal"
          background
          layout="total, sizes, prev, pager, next"
          @size-change="handleLogsPageSizeChange"
          @current-change="handleLogsPageChange"
        />
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="logsVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { getAgentPackages, updateCommission, clearCommission, getCommissionLogs } from '@/api/iccid-commission'

export default {
  name: 'CommissionDialog',
  data() {
    const validateCommissionValue = (rule, value, callback) => {
      if (value === null || value === undefined) {
        callback(new Error('请输入分佣值'))
      } else if (value <= 0) {
        callback(new Error('分佣值必须大于0'))
      } else if (this.commissionForm.commission_type === 1 && value > 100) {
        callback(new Error('百分比不能超过100'))
      } else {
        callback()
      }
    }

    return {
      dialogVisible: false,
      loading: false,
      shopId: null,
      shopName: '',
      packageList: [],

      // 设置分佣
      setCommissionVisible: false,
      submitLoading: false,
      currentPackage: {},
      commissionForm: {
        commission_type: 1,
        commission_value: null,
        adjust_reason: ''
      },
      commissionRules: {
        commission_type: [
          { required: true, message: '请选择分佣类型', trigger: 'change' }
        ],
        commission_value: [
          { required: true, validator: validateCommissionValue, trigger: 'blur' }
        ]
      },

      // 调整记录
      logsVisible: false,
      logsLoading: false,
      logsList: [],
      logsTotal: 0,
      logsQuery: {
        page: 1,
        page_size: 10
      },
      currentPackageCode: ''
    }
  },
  methods: {
    // 打开弹窗
    async open(shop) {
      this.dialogVisible = true
      this.shopId = shop.id
      this.shopName = shop.agent_name
      await this.fetchPackages()
    },

    // 获取套餐列表
    async fetchPackages() {
      this.loading = true
      try {
        const { data } = await getAgentPackages({
          agent_id: this.shopId
        })
        // 数据格式示例：
        // [{
        //   package_code: 'HK05-10GB',
        //   package_name: '香港5天10GB',
        //   price: 98,
        //   default_commission_type: 1,      // 默认分佣类型
        //   default_commission_value: 10,    // 默认分佣值
        //   commission_type: 1,              // 特殊分佣类型（可能为null）
        //   commission_value: 15,            // 特殊分佣值（可能为null）
        //   sales_count: 100,                // 销售数量
        //   sales_amount: 9800,              // 销售金额
        //   total_commission: 1470           // 总佣金
        // }]
        this.packageList = data || []
      } catch (error) {
        this.$message.error('获取套餐列表失败')
        this.packageList = []
      } finally {
        this.loading = false
      }
    },

    // 关闭弹窗
    handleClose() {
      this.dialogVisible = false
      this.shopId = null
      this.shopName = ''
      this.packageList = []
    },

    // 设置特殊分佣
    handleSetCommission(row) {
      this.currentPackage = row
      this.commissionForm = {
        commission_type: row.commission_type || 1,
        commission_value: row.commission_value || null,
        adjust_reason: ''
      }
      this.setCommissionVisible = true
    },

    // 提交分佣设置
    async handleSubmitCommission() {
      this.$refs.commissionForm.validate(async valid => {
        if (!valid) return

        this.submitLoading = true
        try {
          await updateCommission({
            agent_id: this.shopId,
            package_code: this.currentPackage.package_code,
            commission_type: this.commissionForm.commission_type,
            commission_value: this.commissionForm.commission_value,
            adjust_reason: this.commissionForm.adjust_reason
          })

          this.$message.success('分佣设置成功')
          this.setCommissionVisible = false
          await this.fetchPackages()
        } catch (error) {
          this.$message.error(error.message || '设置失败')
        } finally {
          this.submitLoading = false
        }
      })
    },

    // 清除特殊分佣
    handleClearCommission(row) {
      this.$confirm('确认清除该套餐的特殊分佣配置吗？清除后将使用默认分佣。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await clearCommission({
            agent_id: this.shopId,
            package_code: row.package_code,
            adjust_reason: '清除特殊分佣配置'
          })
          this.$message.success('已清除特殊分佣配置')
          await this.fetchPackages()
        } catch (error) {
          this.$message.error(error.message || '操作失败')
        }
      }).catch(() => {})
    },

    // 查看调整记录
    async handleViewLogs(row) {
      this.currentPackageCode = row.package_code
      this.logsQuery.page = 1
      this.logsVisible = true
      await this.fetchLogs()
    },

    // 获取调整记录
    async fetchLogs() {
      this.logsLoading = true
      try {
        const { data } = await getCommissionLogs({
          agent_id: this.shopId,
          package_code: this.currentPackageCode,
          page: this.logsQuery.page,
          page_size: this.logsQuery.page_size
        })
        this.logsList = data.list || []
        this.logsTotal = data.total || 0
      } catch (error) {
        this.$message.error('获取调整记录失败')
        this.logsList = []
        this.logsTotal = 0
      } finally {
        this.logsLoading = false
      }
    },

    // 调整记录分页
    handleLogsPageSizeChange(val) {
      this.logsQuery.page_size = val
      this.fetchLogs()
    },
    handleLogsPageChange(val) {
      this.logsQuery.page = val
      this.fetchLogs()
    }
  }
}
</script>

<style scoped>
.dialog-footer {
  text-align: right;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}
</style>
